import React, { useState, useEffect } from 'react';
import MenuItem from './MenuItem';
import { useNavigate } from 'react-router-dom';
import { isAdmin } from '../services/authService';
import {
  FaUsers,
  FaRunning,
  FaUserCircle,
  FaTachometerAlt,
  FaCog,
  FaTrophy,
  FaBars,
  FaChevronLeft,
  FaPalette,
  FaChartBar,
  FaListOl,
  FaSyncAlt,
  FaUsersCog,
  FaChevronRight,
  FaFlag
} from 'react-icons/fa';

interface MenuProps {
  activeItem: string;
  onMenuItemClick: (item: string) => void;
  onSidebarToggle?: (isCollapsed: boolean) => void;
  initialCollapsed?: boolean;
}

const Menu: React.FC<MenuProps> = ({ activeItem, onMenuItemClick, onSidebarToggle, initialCollapsed = false }) => {
  const navigate = useNavigate();
  const userIsAdmin = isAdmin();
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [activeSubItem, setActiveSubItem] = useState<string>('');
  
  // Lưu trạng thái thu gọn vào localStorage và cập nhật khi prop thay đổi
  useEffect(() => {
    const savedState = localStorage.getItem('sidebar_collapsed');
    if (savedState) {
      setCollapsed(savedState === 'true');
    } else if (initialCollapsed !== undefined) {
      setCollapsed(initialCollapsed);
    }
  }, [initialCollapsed]);
  
  // Danh sách menu items dựa trên quyền người dùng
  const menuItems = [
    // Chỉ hiển thị Dashboard cho admin
    ...(userIsAdmin ? [{ id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: <FaTachometerAlt size={18} /> }] : []),
    { id: 'tournaments', label: 'Giải Đấu', path: '/tournaments', icon: <FaTrophy size={18} /> },
    { id: 'teams', label: 'Teams', path: '/teams', icon: <FaUsers size={18} /> },
    { id: 'activities', label: 'Hoạt động', path: '/activities', icon: <FaRunning size={18} /> },
    { id: 'statistics', label: 'Thống kê', path: '/statistics', icon: <FaChartBar size={18} /> },
    { id: 'profile', label: 'Hồ sơ', path: '/profile', icon: <FaUserCircle size={18} /> },
    // Chỉ hiển thị trang đồng bộ cho admin
    ...(userIsAdmin ? [
      { id: 'sync', label: 'Đồng bộ', path: '/sync', icon: <FaSyncAlt size={18} /> },
      { 
        id: 'management', 
        label: 'Quản lý', 
        path: '#', 
        icon: <FaCog size={18} />,
        subItems: [
          { id: 'user-management', label: 'Quản lý người dùng', path: '/user-management' },
          { id: 'flag-management', label: 'Quản lý gán cờ', path: '/flag-management' }
        ]
      }
    ] : [])
  ];

  const handleItemClick = (item: string, path: string) => {
    // Nếu path là '#' thì không điều hướng (menu có submenu)
    if (path !== '#') {
      // Cập nhật state trước
      onMenuItemClick(item);
      // Điều hướng ngay lập tức
      navigate(path);
    } else {
      // Chỉ cập nhật activeItem mà không điều hướng
      onMenuItemClick(item);
    }
  };

  const handleSubItemClick = (id: string, path: string) => {
    setActiveSubItem(id);
    onMenuItemClick(id);
    navigate(path);
  };

  const toggleCollapse = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem('sidebar_collapsed', newState.toString());
    
    // Thông báo cho component cha về trạng thái sidebar
    if (onSidebarToggle) {
      onSidebarToggle(newState);
    }
  };

  // Kiểm tra xem một menu có phải là menu cha của activeItem hiện tại không
  const isParentOfActive = (menuItem: any): boolean => {
    if (!menuItem.subItems) return false;
    return menuItem.subItems.some((subItem: any) => subItem.id === activeItem);
  };

  return (
    <div className={`dashboard-sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="logo-container">
          {!collapsed && (
            <img 
              src="/Vis-tracker-logo.png" 
              alt="Vissoft Tracker Logo" 
              style={{ 
                height: '60px', 
                borderRadius: 'var(--border-radius-md)',
                margin: '0 auto',
                display: 'block'
              }}
            />
          )}
        </div>
        
        {/* Nút toggle với icon phù hợp theo trạng thái */}
        <button 
          className={`sidebar-toggle-btn ${collapsed ? 'collapsed' : ''}`}
          onClick={toggleCollapse}
          title={collapsed ? "Mở rộng" : "Thu gọn"}
        >
          {collapsed ? <FaChevronRight size={16} /> : <FaChevronLeft size={16} />}
        </button>
      </div>
      <ul className="sidebar-menu">
        {menuItems.map((item: any) => (
          <MenuItem
            key={item.id}
            label={item.label}
            icon={item.icon}
            isActive={activeItem === item.id || isParentOfActive(item)}
            onClick={() => handleItemClick(item.id, item.path)}
            collapsed={collapsed}
            subItems={item.subItems}
            onSubItemClick={handleSubItemClick}
            activeSubItem={activeSubItem}
          />
        ))}
      </ul>
    </div>
  );
};

export default Menu; 